import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader, Card, CardHeader,
  Button, FormGroup, Input, Select,
  EmptyState, Badge, Muted,
} from "../ui";

// ============================================================
// Types
// ============================================================

interface SellableFormat {
  id: string;
  label: string;
  size: string;
  unit: string;
  price: number;
}

interface Product {
  id: string;
  name: string;
  brand: string;
  sellable_formats: SellableFormat[];
}

interface QuickButton {
  id: string;
  group_id: string;
  label: string;
  product_id: string;
  product_name?: string;
  sellable_format_id: string;
  sellable_format_label?: string;
  display_order: number;
  color: string | null;
}

interface QuickButtonGroup {
  id: string;
  name: string;
  display_order: number;
  icon: string | null;
  color: string | null;
  buttons: QuickButton[];
}

// ============================================================
// Color presets
// ============================================================

const COLOR_PRESETS = [
  { label: "Navy", value: "#1B2A4A" },
  { label: "Gold", value: "#F6BE00" },
  { label: "Blue", value: "#8BB8E8" },
  { label: "Green", value: "#2D8A4E" },
  { label: "Red", value: "#C53030" },
  { label: "Purple", value: "#7C3AED" },
  { label: "Orange", value: "#DD6B20" },
  { label: "Teal", value: "#319795" },
];

// ============================================================
// Component
// ============================================================

export default function QuickButtonManager() {
  const [groups, setGroups] = useState<QuickButtonGroup[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Group form state
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<QuickButtonGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: "", display_order: "0", color: "" });

  // Button form state
  const [addingButtonToGroup, setAddingButtonToGroup] = useState<string | null>(null);
  const [editingButton, setEditingButton] = useState<QuickButton | null>(null);
  const [buttonForm, setButtonForm] = useState({
    label: "", product_id: "", sellable_format_id: "", display_order: "0", color: "",
  });

  // Delete confirmation
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<string | null>(null);

  // ============================================================
  // Data loading
  // ============================================================

  const loadData = useCallback(async () => {
    try {
      const [groupsRes, productsRes] = await Promise.all([
        api.get("/quick-buttons"),
        api.get("/products?is_active=true"),
      ]);
      setGroups(groupsRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      console.error("Failed to load quick button data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================================
  // Group CRUD
  // ============================================================

  const openGroupForm = (group?: QuickButtonGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        name: group.name,
        display_order: String(group.display_order),
        color: group.color || "",
      });
    } else {
      setEditingGroup(null);
      setGroupForm({ name: "", display_order: String(groups.length), color: "" });
    }
    setShowGroupForm(true);
  };

  const closeGroupForm = () => {
    setShowGroupForm(false);
    setEditingGroup(null);
  };

  const saveGroup = async () => {
    const payload = {
      name: groupForm.name,
      display_order: parseInt(groupForm.display_order) || 0,
      icon: null,
      color: groupForm.color || null,
    };

    try {
      if (editingGroup) {
        await api.put(`/quick-buttons/groups/${editingGroup.id}`, payload);
      } else {
        await api.post("/quick-buttons/groups", payload);
      }
      closeGroupForm();
      await loadData();
    } catch (err) {
      console.error("Failed to save group:", err);
    }
  };

  const deleteGroup = async (id: string) => {
    try {
      await api.delete(`/quick-buttons/groups/${id}`);
      setConfirmDeleteGroup(null);
      await loadData();
    } catch (err) {
      console.error("Failed to delete group:", err);
    }
  };

  // ============================================================
  // Button CRUD
  // ============================================================

  const openButtonForm = (groupId: string, button?: QuickButton) => {
    if (button) {
      setEditingButton(button);
      setButtonForm({
        label: button.label,
        product_id: button.product_id,
        sellable_format_id: button.sellable_format_id,
        display_order: String(button.display_order),
        color: button.color || "",
      });
      setAddingButtonToGroup(groupId);
    } else {
      setEditingButton(null);
      const group = groups.find((g) => g.id === groupId);
      setButtonForm({
        label: "",
        product_id: "",
        sellable_format_id: "",
        display_order: String(group?.buttons.length || 0),
        color: "",
      });
      setAddingButtonToGroup(groupId);
    }
  };

  const closeButtonForm = () => {
    setAddingButtonToGroup(null);
    setEditingButton(null);
  };

  const saveButton = async () => {
    if (!addingButtonToGroup) return;

    const payload = {
      group_id: addingButtonToGroup,
      label: buttonForm.label,
      product_id: buttonForm.product_id,
      sellable_format_id: buttonForm.sellable_format_id,
      display_order: parseInt(buttonForm.display_order) || 0,
      color: buttonForm.color || null,
    };

    try {
      if (editingButton) {
        await api.put(`/quick-buttons/buttons/${editingButton.id}`, payload);
      } else {
        await api.post("/quick-buttons/buttons", payload);
      }
      closeButtonForm();
      await loadData();
    } catch (err) {
      console.error("Failed to save button:", err);
    }
  };

  const deleteButton = async (id: string) => {
    try {
      await api.delete(`/quick-buttons/buttons/${id}`);
      await loadData();
    } catch (err) {
      console.error("Failed to delete button:", err);
    }
  };

  const moveButton = async (button: QuickButton, direction: "up" | "down") => {
    const group = groups.find((g) => g.id === button.group_id);
    if (!group) return;

    const sorted = [...group.buttons].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((b) => b.id === button.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    try {
      await Promise.all([
        api.put(`/quick-buttons/buttons/${sorted[idx].id}`, {
          ...sorted[idx],
          display_order: sorted[swapIdx].display_order,
        }),
        api.put(`/quick-buttons/buttons/${sorted[swapIdx].id}`, {
          ...sorted[swapIdx],
          display_order: sorted[idx].display_order,
        }),
      ]);
      await loadData();
    } catch (err) {
      console.error("Failed to reorder buttons:", err);
    }
  };

  // ============================================================
  // Helpers
  // ============================================================

  const selectedProduct = products.find((p) => p.id === buttonForm.product_id);

  const handleProductChange = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    setButtonForm((prev) => ({
      ...prev,
      product_id: productId,
      sellable_format_id: "",
      label: prev.label || (product ? product.name : ""),
    }));
  };

  const handleFormatChange = (formatId: string) => {
    const format = selectedProduct?.sellable_formats.find((f) => f.id === formatId);
    setButtonForm((prev) => {
      const productName = selectedProduct?.name || "";
      const autoLabel = format ? `${productName} ${format.label}` : productName;
      return {
        ...prev,
        sellable_format_id: formatId,
        label: prev.label === "" || prev.label === productName ? autoLabel : prev.label,
      };
    });
  };

  // ============================================================
  // Render
  // ============================================================

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Quick Buttons" subtitle="Loading..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Quick Buttons"
        subtitle="Configure quick-access buttons for the POS register"
        action={
          <Button onClick={() => openGroupForm()} variant="primary">
            + Add Group
          </Button>
        }
      />

      {/* Group Form Modal */}
      {showGroupForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(27,42,74,0.5)" }}
          onClick={closeGroupForm}
        >
          <div
            className="rounded-2xl p-6 w-full max-w-md"
            style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
              {editingGroup ? "Edit Group" : "New Quick Button Group"}
            </h3>
            <div className="space-y-4">
              <FormGroup label="Group Name">
                <Input
                  value={groupForm.name}
                  onChange={(v) => setGroupForm((f) => ({ ...f, name: v }))}
                  placeholder="e.g. Slushies, Growlers, Big Cans"
                />
              </FormGroup>
              <FormGroup label="Display Order">
                <Input
                  value={groupForm.display_order}
                  onChange={(v) => setGroupForm((f) => ({ ...f, display_order: v }))}
                  type="number"
                  min="0"
                />
              </FormGroup>
              <FormGroup label="Color">
                <div className="flex flex-wrap gap-2 mb-2">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setGroupForm((f) => ({ ...f, color: c.value }))}
                      className="w-8 h-8 rounded-lg transition-all"
                      style={{
                        background: c.value,
                        border: groupForm.color === c.value ? "3px solid var(--rbw-gold)" : "2px solid var(--rbw-border)",
                        transform: groupForm.color === c.value ? "scale(1.15)" : "scale(1)",
                      }}
                      title={c.label}
                    />
                  ))}
                </div>
              </FormGroup>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <Button variant="ghost" onClick={closeGroupForm}>Cancel</Button>
              <Button onClick={saveGroup} disabled={!groupForm.name.trim()}>
                {editingGroup ? "Save Changes" : "Create Group"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Button Form Modal */}
      {addingButtonToGroup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(27,42,74,0.5)" }}
          onClick={closeButtonForm}
        >
          <div
            className="rounded-2xl p-6 w-full max-w-md"
            style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
              {editingButton ? "Edit Button" : "Add Quick Button"}
            </h3>
            <div className="space-y-4">
              <FormGroup label="Product">
                <Select
                  value={buttonForm.product_id}
                  onChange={handleProductChange}
                >
                  <option value="">Select a product...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.brand ? `${p.brand} - ${p.name}` : p.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              {selectedProduct && (
                <FormGroup label="Sellable Format">
                  <Select
                    value={buttonForm.sellable_format_id}
                    onChange={handleFormatChange}
                  >
                    <option value="">Select a format...</option>
                    {selectedProduct.sellable_formats.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label} - ${f.price.toFixed(2)}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              )}

              <FormGroup label="Button Label">
                <Input
                  value={buttonForm.label}
                  onChange={(v) => setButtonForm((f) => ({ ...f, label: v }))}
                  placeholder="Label shown on register"
                />
              </FormGroup>

              <FormGroup label="Display Order">
                <Input
                  value={buttonForm.display_order}
                  onChange={(v) => setButtonForm((f) => ({ ...f, display_order: v }))}
                  type="number"
                  min="0"
                />
              </FormGroup>

              <FormGroup label="Button Color">
                <div className="flex flex-wrap gap-2 mb-2">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setButtonForm((f) => ({ ...f, color: c.value }))}
                      className="w-8 h-8 rounded-lg transition-all"
                      style={{
                        background: c.value,
                        border: buttonForm.color === c.value ? "3px solid var(--rbw-gold)" : "2px solid var(--rbw-border)",
                        transform: buttonForm.color === c.value ? "scale(1.15)" : "scale(1)",
                      }}
                      title={c.label}
                    />
                  ))}
                </div>
              </FormGroup>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <Button variant="ghost" onClick={closeButtonForm}>Cancel</Button>
              <Button
                onClick={saveButton}
                disabled={!buttonForm.label.trim() || !buttonForm.product_id || !buttonForm.sellable_format_id}
              >
                {editingButton ? "Save Changes" : "Add Button"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation */}
      {confirmDeleteGroup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(27,42,74,0.5)" }}
          onClick={() => setConfirmDeleteGroup(null)}
        >
          <div
            className="rounded-2xl p-6 w-full max-w-sm"
            style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
              Delete Group?
            </h3>
            <p className="text-sm mb-6" style={{ color: "var(--rbw-gray-dark)" }}>
              This will permanently delete the group and all its buttons. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setConfirmDeleteGroup(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => deleteGroup(confirmDeleteGroup)}>Delete Group</Button>
            </div>
          </div>
        </div>
      )}

      {/* Groups */}
      {groups.length === 0 ? (
        <EmptyState
          icon="grid"
          message="No quick button groups yet"
          detail="Create a group to start adding quick buttons to the register"
        />
      ) : (
        <div className="space-y-6">
          {[...groups]
            .sort((a, b) => a.display_order - b.display_order)
            .map((group) => (
              <Card key={group.id}>
                <CardHeader
                  title={group.name}
                  action={
                    <div className="flex items-center gap-2">
                      <Badge variant={group.buttons.length > 0 ? "blue" : "default"}>
                        {group.buttons.length} button{group.buttons.length !== 1 ? "s" : ""}
                      </Badge>
                      {group.color && (
                        <div
                          className="w-5 h-5 rounded-md"
                          style={{ background: group.color, border: "1px solid var(--rbw-border)" }}
                          title={`Group color: ${group.color}`}
                        />
                      )}
                      <Muted>Order: {group.display_order}</Muted>
                      <Button size="sm" variant="ghost" onClick={() => openGroupForm(group)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteGroup(group.id)}>
                        <span style={{ color: "var(--rbw-danger)" }}>Delete</span>
                      </Button>
                    </div>
                  }
                />

                {/* Buttons grid */}
                {group.buttons.length === 0 ? (
                  <div className="text-center py-8">
                    <Muted>No buttons in this group yet</Muted>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                    {[...group.buttons]
                      .sort((a, b) => a.display_order - b.display_order)
                      .map((btn, idx) => (
                        <div
                          key={btn.id}
                          className="relative rounded-xl p-3 text-center group cursor-pointer transition-all"
                          style={{
                            background: btn.color || group.color || "var(--rbw-navy)",
                            border: "2px solid transparent",
                            minHeight: "80px",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.border = "2px solid var(--rbw-gold)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.border = "2px solid transparent";
                          }}
                        >
                          {/* Action overlay */}
                          <div
                            className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-start justify-end gap-1 p-1.5"
                            style={{ background: "rgba(0,0,0,0.3)" }}
                          >
                            {idx > 0 && (
                              <button
                                onClick={() => moveButton(btn, "up")}
                                className="w-6 h-6 rounded flex items-center justify-center text-white"
                                style={{ background: "rgba(255,255,255,0.2)" }}
                                title="Move left"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                            )}
                            {idx < group.buttons.length - 1 && (
                              <button
                                onClick={() => moveButton(btn, "down")}
                                className="w-6 h-6 rounded flex items-center justify-center text-white"
                                style={{ background: "rgba(255,255,255,0.2)" }}
                                title="Move right"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => openButtonForm(group.id, btn)}
                              className="w-6 h-6 rounded flex items-center justify-center text-white"
                              style={{ background: "rgba(255,255,255,0.2)" }}
                              title="Edit"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => deleteButton(btn.id)}
                              className="w-6 h-6 rounded flex items-center justify-center text-white"
                              style={{ background: "rgba(197,48,48,0.7)" }}
                              title="Delete"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>

                          {/* Button content */}
                          <div className="flex flex-col items-center justify-center h-full">
                            <div
                              className="text-sm font-bold leading-tight"
                              style={{ color: isLightColor(btn.color || group.color || "#1B2A4A") ? "var(--rbw-navy)" : "#fff" }}
                            >
                              {btn.label}
                            </div>
                            {btn.product_name && (
                              <div
                                className="text-[10px] mt-1 opacity-75"
                                style={{ color: isLightColor(btn.color || group.color || "#1B2A4A") ? "var(--rbw-navy)" : "#fff" }}
                              >
                                {btn.product_name}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                <Button size="sm" variant="ghost" onClick={() => openButtonForm(group.id)}>
                  + Add Button
                </Button>
              </Card>
            ))}
        </div>
      )}
    </PageContainer>
  );
}

// ============================================================
// Utility
// ============================================================

function isLightColor(hex: string): boolean {
  const c = hex.replace("#", "");
  if (c.length !== 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
}
